import { useState, useEffect } from "react";
import { S, api } from "./shared";

// ── Reservations ──────────────────────────────────────────
// Self-contained: fetches and manages its own data, so the exact same
// component can be dropped into both the worker nav tab and the admin
// panel and they'll always show the same shared list. Split into its own
// file just to keep App.js from growing without bound — nothing about how
// it's used from App.js changes.
const emptyReservation = { eventName:"", date:"", guestName:"", guestEmail:"", guestPhone:"", specialRequests:"", tickets:"0", tableReserved:false };

const ReservationForm = ({ form, setForm, t, onSave, onCancel, busy }) => (
  <div style={{padding:"10px", background:"var(--color-background-secondary)", borderRadius:"8px", marginBottom:"10px"}}>
    <input style={{...S.inp, marginBottom:"6px"}} placeholder={t.eventName} value={form.eventName}
      onChange={e => setForm(p=>({...p, eventName:e.target.value}))} />
    <input style={{...S.inp, marginBottom:"4px"}} placeholder="DD.MM.YYYY" value={form.date}
      onChange={e => setForm(p=>({...p, date:e.target.value}))} />
    <p style={{...S.dimText, marginBottom:"6px"}}>{t.formatHint}</p>
    <input style={{...S.inp, marginBottom:"6px"}} placeholder={t.guestName} value={form.guestName}
      onChange={e => setForm(p=>({...p, guestName:e.target.value}))} />
    <input style={{...S.inp, marginBottom:"6px"}} placeholder={t.guestEmail} value={form.guestEmail}
      onChange={e => setForm(p=>({...p, guestEmail:e.target.value}))} />
    <input style={{...S.inp, marginBottom:"4px"}} placeholder={t.guestPhone} value={form.guestPhone}
      onChange={e => setForm(p=>({...p, guestPhone:e.target.value}))} />
    <p style={{...S.dimText, marginBottom:"6px"}}>{t.contactHint}</p>
    <textarea style={{...S.inp, marginBottom:"6px", minHeight:"50px", resize:"vertical", fontFamily:"inherit"}}
      placeholder={t.specialRequests} value={form.specialRequests}
      onChange={e => setForm(p=>({...p, specialRequests:e.target.value}))} />
    <div style={{display:"flex", gap:"8px", alignItems:"center", marginBottom:"8px"}}>
      <label style={{fontSize:"13px", color:"var(--color-text-secondary)"}}>{t.tickets}</label>
      <input type="number" min="0" style={{...S.inp, width:"80px"}} value={form.tickets}
        onChange={e => setForm(p=>({...p, tickets:e.target.value}))} />
    </div>
    <label style={{display:"flex", alignItems:"center", gap:"8px", fontSize:"13px", color:"var(--color-text-secondary)", marginBottom:"10px"}}>
      <input type="checkbox" checked={form.tableReserved}
        onChange={e => setForm(p=>({...p, tableReserved:e.target.checked}))} />
      {t.tableReserved}
    </label>
    <div style={{display:"flex", gap:"8px"}}>
      <button style={S.btn} onClick={onSave} disabled={busy}>{t.saveEdit}</button>
      <button style={{...S.btn,background:"var(--color-background-primary)",color:"var(--color-text-primary)"}}
        onClick={onCancel}>{t.cancelEdit}</button>
    </div>
  </div>
);

export const ReservationsView = ({ t, showToast }) => {
  const [list, setList] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [formTarget, setFormTarget] = useState(null); // null | "new" | <reservation id>
  const [form, setForm] = useState(emptyReservation);
  const [busy, setBusy] = useState(false);

  const load = () => api("getReservations")
    .then(data => { setList(data); setLoaded(true); })
    .catch(() => showToast(t.error));

  // Intentionally runs once on mount only. load()/showToast aren't memoized
  // in App.js, so listing them as deps would refetch on every unrelated
  // re-render — a plain "eslint-disable-next-line" (no rule name) is used
  // instead of naming react-hooks/exhaustive-deps, since this build failed
  // with "Definition for rule 'react-hooks/exhaustive-deps' was not found"
  // when that rule was named explicitly.
  // eslint-disable-next-line
  useEffect(() => { load(); }, []);

  const startAdd = () => { setForm(emptyReservation); setFormTarget("new"); };
  const startEdit = r => {
    setForm({
      eventName:r.eventName, date:r.date, guestName:r.guestName,
      guestEmail:r.guestEmail||"", guestPhone:r.guestPhone||"",
      specialRequests:r.specialRequests||"", tickets:String(r.tickets),
      tableReserved: !!r.tableReserved
    });
    setFormTarget(r.id);
  };
  const cancelForm = () => setFormTarget(null);

  const save = async () => {
    if (!form.eventName.trim() || !form.date.trim() || !form.guestName.trim()) {
      showToast(t.reservationRequiredFields); return;
    }
    if (!form.guestEmail.trim() && !form.guestPhone.trim()) {
      showToast(t.reservationContactRequired); return;
    }
    setBusy(true);
    try {
      const payload = { ...form, tickets: Number(form.tickets) || 0 };
      if (formTarget === "new") await api("addReservation", "POST", payload);
      else await api("updateReservation", "POST", { ...payload, id: formTarget });
      setFormTarget(null);
      showToast(t.sessionSaved);
      load();
    } catch(e) { showToast(e.detail || t.error); }
    finally { setBusy(false); }
  };

  const remove = async id => {
    if (!window.confirm(t.confirmDeleteReservation)) return;
    setBusy(true);
    try {
      await api("deleteReservation", "DELETE", { id });
      setList(p => p.filter(r => r.id !== id));
    } catch(e) { showToast(e.detail || t.error); }
    finally { setBusy(false); }
  };

  return (
    <div style={S.card}>
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"10px"}}>
        <p style={{...S.sec, margin:0}}>{t.reservations}</p>
        <div style={{display:"flex", gap:"6px"}}>
          <button style={S.btnSm} onClick={load} disabled={busy}>↻ {t.refresh}</button>
          {formTarget !== "new" && <button style={S.btnSm} onClick={startAdd}>+ {t.addReservation}</button>}
        </div>
      </div>

      {formTarget === "new" && (
        <ReservationForm form={form} setForm={setForm} t={t} onSave={save} onCancel={cancelForm} busy={busy} />
      )}

      {!loaded && <p style={S.dimText}>{t.loading}</p>}
      {loaded && list.length === 0 && <p style={{fontSize:"13px",color:"var(--color-text-secondary)"}}>{t.noReservations}</p>}

      {list.map(r => (
        <div key={r.id} style={{padding:"10px 0", borderBottom:"0.5px solid var(--color-border-tertiary)"}}>
          {formTarget === r.id ? (
            <ReservationForm form={form} setForm={setForm} t={t} onSave={save} onCancel={cancelForm} busy={busy} />
          ) : (
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:"8px"}}>
              <div>
                <p style={{margin:0, fontWeight:500, fontSize:"14px", color:"var(--color-text-primary)"}}>{r.eventName} — {r.date}</p>
                <p style={{margin:"2px 0 0", fontSize:"13px", color:"var(--color-text-secondary)"}}>{r.guestName}</p>
                {(r.guestEmail || r.guestPhone) && (
                  <p style={{margin:"2px 0 0", fontSize:"12px", color:"var(--color-text-tertiary)"}}>
                    {[r.guestEmail, r.guestPhone].filter(Boolean).join(" · ")}
                  </p>
                )}
                {r.specialRequests && <p style={{margin:"2px 0 0", fontSize:"12px", color:"var(--color-text-tertiary)"}}>{r.specialRequests}</p>}
                <p style={{margin:"4px 0 0", fontSize:"12px", color:"var(--color-text-secondary)"}}>
                  {t.tickets}: {r.tickets} · {t.tableReserved} {r.tableReserved ? t.yes : t.no}
                </p>
              </div>
              <div style={{display:"flex", gap:"6px", flexShrink:0}}>
                <button style={{...S.btnSm,color:"#2d6a4f"}} onClick={() => startEdit(r)}>{t.editSession}</button>
                <button style={{...S.btnSm,color:"var(--color-text-danger)"}} onClick={() => remove(r.id)}>{t.deleteSession}</button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};